import React, { useContext, useEffect, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Table } from "reactstrap";
import Header from "../components/Headers/Header";
import AdminNavbar from "../components/Navbars/AdminNavbar/AdminNavbar";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import { AuthContext } from "../context/AuthContext";
import useDashboard from "../context/hooks/useDashboard";
import useUser from "../context/hooks/useUser";
import { PermissionModule } from "../types/permissions";
import type { AgendaDashboardType } from "../context/hooks/useDashboard";

function formatDateTime(iso: string | null): string {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function formatCurrency(value: number): string {
    return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

interface StatCardProps {
    icon: string;
    label: string;
    value: number | string;
    color: string;
    description?: string;
}

function StatCard({ icon, label, value, color, description }: StatCardProps): React.ReactElement {
    return (
        <Card className="shadow border-0 h-100">
            <CardBody>
                <Row className="align-items-center">
                    <Col className="col-auto">
                        <div
                            className="icon icon-shape rounded-circle shadow d-flex align-items-center justify-content-center"
                            style={{ background: color, width: 48, height: 48, minWidth: 48 }}
                        >
                            <i className={`${icon} text-white`} style={{ fontSize: 20 }} />
                        </div>
                    </Col>
                    <Col>
                        <span className="text-muted text-uppercase mb-1" style={{ fontSize: 11, fontWeight: 600 }}>
                            {label}
                        </span>
                        <h3 className="mb-0">{value}</h3>
                        {description && (
                            <small className="text-muted d-block mt-1">{description}</small>
                        )}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
}

interface AgendaRowProps {
    agenda: AgendaDashboardType;
}

function AgendaRow({ agenda }: AgendaRowProps): React.ReactElement {
    return (
        <tr>
            <td>{agenda.description ?? "—"}</td>
            <td>{agenda.location ?? "—"}</td>
            <td>{formatDateTime(agenda.startDate)}</td>
            <td>{formatDateTime(agenda.endDate)}</td>
            <td>
                {agenda.tipoAgendaName
                    ? <Badge color="info" pill>{agenda.tipoAgendaName}</Badge>
                    : "—"}
            </td>
            <td>
                {agenda.cursoName
                    ? <Badge color="primary" pill>{agenda.cursoName}</Badge>
                    : "—"}
            </td>
        </tr>
    );
}

const HomeEmpresa: React.FC = () => {
    const { objUserTk } = useContext(AuthContext);
    const { empresaDashboard, isDashboardLoading, fetchEmpresaDashboard } = useDashboard();
    const { getPermissionsByUser, modulePermissions } = useUser();

    const empresaId = useMemo(() => objUserTk.EmpresaId, [objUserTk]);
    const userId = useMemo(() => objUserTk.UserId, [objUserTk]);

    useEffect(() => {
        if (!empresaId || !userId) return;
        Promise.all([
            fetchEmpresaDashboard(empresaId),
            getPermissionsByUser(userId),
        ]);
    }, [empresaId, userId]);

    const temFinanceiro = useMemo(
        () => modulePermissions.some(mp => mp.module === PermissionModule.Financeiro),
        [modulePermissions]
    );

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText="Página Inicial" search={false} titleSearch="" />

            <Container className="mt-4" fluid>
                {isDashboardLoading || !empresaDashboard ? (
                    <Row className="justify-content-center mt-5">
                        <Col className="text-center text-muted">
                            <div className="mb-3">Carregando dados...</div>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <div className="mb-3">
                            <h5 className="mb-1">Visão Geral de Contratos</h5>
                            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                                Dados consolidados de todos os contratos cadastrados no sistema
                            </p>
                        </div>

                        <Row className="g-3 mb-4">
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    icon="fa-solid fa-file-lines"
                                    label="Contratos Abertos"
                                    value={empresaDashboard.contratosAbertos}
                                    color="#5e72e4"
                                    description="Contratos em andamento"
                                />
                            </Col>
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    icon="fa-solid fa-hourglass-half"
                                    label="Em Preparação"
                                    value={empresaDashboard.contratosEmPreparacao}
                                    color="#fb6340"
                                    description="Aguardando processamento"
                                />
                            </Col>
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    icon="fa-solid fa-check-circle"
                                    label="Contratos Finalizados"
                                    value={empresaDashboard.contratosFinalizados}
                                    color="#2dce89"
                                    description="Concluídos com sucesso"
                                />
                            </Col>
                            <Col xl="3" lg="6" md="6">
                                <StatCard
                                    icon="fa-solid fa-graduation-cap"
                                    label="Total de Alunos"
                                    value={empresaDashboard.totalAlunos}
                                    color="#11cdef"
                                    description="Alunos cadastrados"
                                />
                            </Col>
                        </Row>

                        {temFinanceiro && (
                            <>
                                <div className="mb-3 mt-4">
                                    <h5 className="mb-1">Resumo Financeiro</h5>
                                    <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                                        Valores pendentes de contas a pagar do módulo financeiro
                                    </p>
                                </div>

                                <Row className="g-3 mb-4">
                                    <Col xs="12">
                                        <Card className="shadow border-0 h-100">
                                            <CardBody>
                                                <Row className="align-items-center">
                                                    <Col className="col-auto">
                                                        <div
                                                            className="icon icon-shape rounded-circle shadow d-flex align-items-center justify-content-center"
                                                            style={{ background: "#f5365c", width: 48, height: 48, minWidth: 48 }}
                                                        >
                                                            <i className="fa fa-arrow-circle-up text-white" style={{ fontSize: 20 }} />
                                                        </div>
                                                    </Col>
                                                    <Col>
                                                        <span className="text-muted text-uppercase mb-1" style={{ fontSize: 11, fontWeight: 600 }}>
                                                            Contas a Pagar
                                                        </span>
                                                        <h3 className="mb-0 text-danger">
                                                            {formatCurrency(empresaDashboard.totalContaPagarPendente)}
                                                        </h3>
                                                        <small className="text-muted d-block mt-1">
                                                            Total de valores pendentes a pagar para fornecedores
                                                        </small>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        )}

                        <div className="mb-3 mt-4">
                            <h5 className="mb-1">Próximas Agendas</h5>
                            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                                Eventos e agendamentos programados nos próximos dias
                            </p>
                        </div>

                        <Card className="shadow border-0 mb-4">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h5 className="mb-1">Agenda de Eventos</h5>
                                        <span className="text-muted" style={{ fontSize: 13 }}>
                                            {empresaDashboard.proximasAgendas.length === 0 
                                                ? "Nenhum evento programado" 
                                                : `${empresaDashboard.proximasAgendas.length} evento(s) próximo(s)`}
                                        </span>
                                    </div>
                                    <Badge color="primary" pill style={{ fontSize: 14, padding: "8px 16px" }}>
                                        {empresaDashboard.proximasAgendas.length}
                                    </Badge>
                                </div>

                                {empresaDashboard.proximasAgendas.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fa fa-calendar-times mb-3" style={{ fontSize: 42, opacity: 0.4 }} />
                                        <h6 className="text-muted mb-2">Nenhuma agenda cadastrada</h6>
                                        <p className="mb-0" style={{ fontSize: 13 }}>
                                            Não há eventos ou agendamentos programados para os próximos dias.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table className="align-items-center table-flush" hover>
                                            <thead className="thead-light">
                                                <tr>
                                                    <th scope="col">Descrição</th>
                                                    <th scope="col">Local</th>
                                                    <th scope="col">Início</th>
                                                    <th scope="col">Fim</th>
                                                    <th scope="col">Tipo</th>
                                                    <th scope="col">Curso</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {empresaDashboard.proximasAgendas.map((agenda) => (
                                                    <AgendaRow key={agenda.id} agenda={agenda} />
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        <Card className="shadow border-0 bg-secondary mb-4">
                            <CardBody>
                                <Row className="align-items-center">
                                    <Col md="1" className="text-center">
                                        <i className="fa fa-info-circle text-primary" style={{ fontSize: 32 }} />
                                    </Col>
                                    <Col md="11">
                                        <h6 className="mb-1">Sobre esta Dashboard</h6>
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                            Os dados apresentados são atualizados em tempo real através da API do sistema. 
                                            As informações de contratos, alunos e financeiro são consolidadas da base de dados da empresa.
                                        </p>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </>
                )}
            </Container>
        </>
    );
};

export default HomeEmpresa;
